import React, { useEffect } from 'react'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import {
  ButtonAction,
  Priorities,
} from 'Components/SharedComponents/Surveys/types'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { snakeCaseToSpaces } from 'Utils/formatters'
import {
  initialPriorities,
  orgLeaderPathRoles,
  NeedsAssessmentWizardStep,
} from '../../constants'
import { stringToTitleCase } from '../../../Academy/_common/utils'
import { HandleAnyAction } from '../../types'
import { ListItem, UnorderedList } from './WeHaveCapturedYourPriorities.styled'
import { sendStepTransitionEvent } from '../../utils'

type WeHaveCapturedYourPrioritiesProps = {
  onAnyAction: HandleAnyAction
  onSetPriorities: (priorities: Priorities) => void
  priorities: Priorities
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole?: UserSurveyRole
}

export const WeHaveCapturedYourPriorities = ({
  onAnyAction,
  onSetPriorities,
  priorities,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: WeHaveCapturedYourPrioritiesProps) => {
  /*
   Reset optional third priority if user leaves it partially completed and then
   hits "back" or if user is an individual contributor (no third priority option)
   */
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const isOrgLeader = userRole && orgLeaderPathRoles.includes(userRole)

  useEffect(() => {
    const { thirdPriority } = priorities
    const { category: thirdCategory, confidenceLevel } = thirdPriority
    const isThirdPriorityIncomplete =
      (thirdCategory && !confidenceLevel) || (confidenceLevel && !thirdCategory)

    if (
      isThirdPriorityIncomplete ||
      (thirdCategory && userRole === UserSurveyRole.TeamMember)
    ) {
      onSetPriorities({
        ...priorities,
        thirdPriority: initialPriorities.thirdPriority,
      })
    }
  }, [onSetPriorities, priorities, userRole])

  const currentStep = NeedsAssessmentWizardStep.WE_HAVE_CAPTURED_YOUR_PRIORITIES
  const nextStep = NeedsAssessmentWizardStep.ADDITIONAL_PRIORITIES
  const userId = userConfig.id
  const nonprofitOrgId = nonprofitOrgConfig.orgId
  const { funderOrgId } = nonprofitOrgConfig
  const userEmail = userConfig.email
  const nextActions: ButtonAction[] = [
    {
      callback: () => {
        onSetPriorities({
          ...priorities,
          thirdPriority: initialPriorities.thirdPriority,
        })
      },
      isHidden: !isOrgLeader || !priorities.thirdPriority.category,
      label: 'Remove Third Priority',
      type: ActionElementType.BUTTON,
      variant: 'secondary',
    },
    {
      isHidden: !isOrgLeader || !!priorities.thirdPriority.category,
      label: 'Add Priority',
      path: nextStep,
      type: ActionElementType.BUTTON,
      variant: 'secondary',
      callback: () => {
        sendStepTransitionEvent({
          currentStep,
          nextStep,
          userId: userConfig.id,
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          funderOrgId: nonprofitOrgConfig.funderOrgId,
          userEmail: userConfig.email,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
    {
      label: isOrgLeader ? 'View Goals' : 'Next',
      path: isOrgLeader
        ? NeedsAssessmentWizardStep.WE_SUGGEST_THESE_GOALS
        : NeedsAssessmentWizardStep.THANK_YOU_FOR_SHARING,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: () => {
        if (isOrgLeader) {
          sendStepTransitionEvent({
            currentStep,
            nextStep: NeedsAssessmentWizardStep.WE_SUGGEST_THESE_GOALS,
            userId,
            nonprofitOrgId,
            funderOrgId,
            userEmail,
            userPrimaryFunction,
            userJourneyLength,
            userRole,
          })
        } else {
          sendStepTransitionEvent({
            currentStep,
            nextStep: NeedsAssessmentWizardStep.THANK_YOU_FOR_SHARING,
            userId,
            nonprofitOrgId,
            funderOrgId,
            userEmail,
            userPrimaryFunction,
            userJourneyLength,
            userRole,
          })
        }
      },
    },
  ]

  const { firstPriority, secondPriority, thirdPriority } = priorities

  const firstCategory = stringToTitleCase(
    snakeCaseToSpaces(firstPriority.category)
  )

  const secondCategory = stringToTitleCase(
    snakeCaseToSpaces(secondPriority.category)
  )

  const thirdCategory = stringToTitleCase(
    snakeCaseToSpaces(thirdPriority.category)
  )

  const firstPriorityTopics = firstPriority.selectedGoals
  const secondPriorityTopics = secondPriority.selectedGoals
  const thirdPriorityTopics = thirdPriority.selectedGoals

  const numOfPriorities = [firstPriority, secondPriority, thirdPriority].filter(
    (p) => !!p.category
  ).length

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const countMap: Record<number, string> = { 2: 'two', 3: 'three' }

  const title = isOrgLeader
    ? `Your org has ${countMap[numOfPriorities]} priorities!`
    : `We've captured your priorities!`

  const description = isOrgLeader
    ? `Adding priorities lets us customize your experience more. ` +
      `When you're done, we'll recommend a set of goals based on these ` +
      `priorities, and you can select the ones you want to focus on.`
    : ``

  return (
    <StepContainer>
      {title && <Title>{title}</Title>}
      {description && <Description>{description}</Description>}
      {firstCategory && (
        <UnorderedList>
          <>
            <ListItem>
              <strong>First Priority: {firstCategory}</strong>
            </ListItem>
            {!!firstPriorityTopics.length &&
              firstPriorityTopics.map((goal) => (
                <ListItem key={goal.label}>{goal.label}</ListItem>
              ))}
          </>
        </UnorderedList>
      )}
      {secondCategory && (
        <UnorderedList>
          <>
            <ListItem>
              <strong>Second Priority: {secondCategory}</strong>
            </ListItem>
            {!!secondPriorityTopics &&
              secondPriorityTopics.map((goal) => (
                <ListItem key={goal.label}>{goal.label}</ListItem>
              ))}
          </>
        </UnorderedList>
      )}
      {thirdCategory && (
        <UnorderedList>
          <>
            <ListItem>
              <strong>Third Priority: {thirdCategory}</strong>
            </ListItem>
            {!!thirdPriority &&
              thirdPriorityTopics.map((goal) => (
                <ListItem key={goal.label}>{goal.label}</ListItem>
              ))}
          </>
        </UnorderedList>
      )}
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
